import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, MapPin, Search, ChevronRight, ChevronLeft, Plus, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API_URL = window.location.origin;

const KIND_LABEL = {
  markets:   { singular: 'Market',   color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  expos:     { singular: 'Expo',     color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  festivals: { singular: 'Festival', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
};

export const ProvincesModal = ({ isOpen, onClose, token, setMessage }) => {
  const [data, setData] = useState({ provinces: [], items: {}, counts: {} });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeProvince, setActiveProvince] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/categories/provinces`);
      const json = await resp.json();
      if (resp.ok) setData(json);
      else setMessage?.({ type: 'error', text: json.detail || 'Failed to load provinces' });
    } catch {
      setMessage?.({ type: 'error', text: 'Network error loading provinces' });
    } finally {
      setLoading(false);
    }
  }, [setMessage]);

  useEffect(() => { if (isOpen) fetchData(); }, [isOpen, fetchData]);

  const assign = async (kind, name, province) => {
    setBusy(true);
    try {
      const singular = kind.replace(/s$/, '');
      const resp = await fetch(`${API_URL}/api/categories/provinces/assign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ kind: singular, name, province }),
      });
      const json = await resp.json();
      if (resp.ok) {
        setMessage?.({ type: 'success', text: province ? `Moved "${name}" → ${province}` : `Removed "${name}" from province` });
        fetchData();
      } else {
        setMessage?.({ type: 'error', text: json.detail || 'Failed to update' });
      }
    } finally {
      setBusy(false);
    }
  };

  // Unassigned bucket for the right-hand "add" panel
  const unassigned = data.items?.Unassigned || { markets: [], expos: [], festivals: [] };
  const flatUnassigned = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = [
      ...unassigned.markets.map((n) => ({ name: n, kind: 'markets' })),
      ...unassigned.expos.map((n) => ({ name: n, kind: 'expos' })),
      ...unassigned.festivals.map((n) => ({ name: n, kind: 'festivals' })),
    ];
    return q ? all.filter((i) => i.name.toLowerCase().includes(q)) : all;
  }, [unassigned, search]);

  if (!isOpen) return null;

  const activeItems = activeProvince ? (data.items[activeProvince] || { markets: [], expos: [], festivals: [] }) : null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="provinces-modal">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b dark:border-gray-700">
          <div className="flex items-center gap-3">
            {activeProvince && (
              <button onClick={() => setActiveProvince(null)} className="text-gray-500 hover:text-[#FFD12D]" data-testid="prov-back">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div className="w-10 h-10 rounded-xl bg-[#FFD12D]/20 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#FFD12D]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#252525] dark:text-white">
                {activeProvince || 'Provinces'}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {activeProvince
                  ? 'Add or remove markets, expos & festivals'
                  : 'Click a province to manage its assignments'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-[#FFD12D]" data-testid="prov-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center"><p className="text-gray-500">Loading…</p></div>
        ) : !activeProvince ? (
          /* ---------------- PROVINCE LIST ------------------ */
          <div className="flex-1 overflow-auto p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.provinces.map((p) => {
              const c = data.counts[p] || 0;
              return (
                <button
                  key={p}
                  onClick={() => setActiveProvince(p)}
                  className="text-left bg-white dark:bg-[#252525] border-2 border-gray-200 dark:border-gray-700 hover:border-[#FFD12D] rounded-xl p-4 transition-all"
                  data-testid={`prov-card-${p.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <MapPin className="w-5 h-5 text-[#FFD12D]" />
                    <span className="text-2xl font-bold text-[#252525] dark:text-white">{c}</span>
                  </div>
                  <h3 className="font-bold text-[#252525] dark:text-white">{p}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {c === 0 ? 'No items assigned yet' : `${c} location${c === 1 ? '' : 's'}`}
                  </p>
                  <div className="flex items-center justify-end mt-2 text-[#FFD12D] text-xs font-medium">
                    Manage <ChevronRight className="w-3 h-3 ml-1" />
                  </div>
                </button>
              );
            })}
            {/* Unassigned bucket */}
            <button
              onClick={() => setActiveProvince('Unassigned')}
              className="text-left bg-amber-50 dark:bg-amber-900/10 border-2 border-amber-200 dark:border-amber-800 hover:border-amber-500 rounded-xl p-4 transition-all"
              data-testid="prov-card-unassigned"
            >
              <div className="flex items-center justify-between mb-2">
                <Plus className="w-5 h-5 text-amber-600" />
                <span className="text-2xl font-bold text-amber-700 dark:text-amber-300">{data.counts.Unassigned || 0}</span>
              </div>
              <h3 className="font-bold text-amber-700 dark:text-amber-300">Unassigned</h3>
              <p className="text-xs text-amber-600 dark:text-amber-200 mt-1">Items not assigned to any province</p>
            </button>
          </div>
        ) : (
          /* ---------------- PROVINCE DETAIL ---------------- */
          <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-0 divide-x dark:divide-gray-700">
            {/* LEFT: items in this province */}
            <div className="overflow-auto p-5">
              <h3 className="text-sm font-semibold uppercase text-gray-500 dark:text-gray-400 mb-3">
                Assigned to {activeProvince} ({(activeItems.markets.length + activeItems.expos.length + activeItems.festivals.length)})
              </h3>
              {(['markets', 'expos', 'festivals']).map((kind) => (
                activeItems[kind].length > 0 && (
                  <div key={kind} className="mb-4">
                    <div className={`text-xs uppercase font-semibold mb-2 px-2 py-1 rounded ${KIND_LABEL[kind].color} inline-block`}>
                      {KIND_LABEL[kind].singular}s ({activeItems[kind].length})
                    </div>
                    <div className="space-y-1">
                      {activeItems[kind].map((name) => (
                        <div key={`${kind}-${name}`} className="flex items-center justify-between bg-white dark:bg-[#252525] rounded-lg border dark:border-gray-700 px-3 py-2">
                          <span className="text-sm dark:text-white truncate">{name}</span>
                          {activeProvince !== 'Unassigned' && (
                            <button
                              onClick={() => assign(kind, name, null)}
                              disabled={busy}
                              title="Remove from this province"
                              className="text-red-500 hover:text-red-700 ml-2 shrink-0"
                              data-testid={`prov-remove-${kind}-${name}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
              {activeItems.markets.length + activeItems.expos.length + activeItems.festivals.length === 0 && (
                <p className="text-sm text-gray-500 py-8 text-center">Nothing assigned yet. Pick items from the right →</p>
              )}
            </div>

            {/* RIGHT: searchable list of unassigned (or "move to" other-province items) */}
            <div className="overflow-auto p-5 bg-gray-50 dark:bg-[#252525]/50">
              <h3 className="text-sm font-semibold uppercase text-gray-500 dark:text-gray-400 mb-3">
                {activeProvince === 'Unassigned' ? 'Move these items into a province' : `Add items to ${activeProvince}`}
              </h3>

              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 dark:bg-[#1a1a1a] dark:text-white"
                  data-testid="prov-search"
                />
              </div>

              {activeProvince === 'Unassigned' ? (
                /* From Unassigned view: pick a destination province for each item */
                flatUnassigned.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center">All items have a province ✨</p>
                ) : (
                  <div className="space-y-1">
                    {flatUnassigned.map((item) => (
                      <div key={`u-${item.kind}-${item.name}`} className="flex items-center gap-2 bg-white dark:bg-[#1a1a1a] rounded-lg border dark:border-gray-700 px-3 py-2">
                        <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${KIND_LABEL[item.kind].color}`}>
                          {KIND_LABEL[item.kind].singular}
                        </span>
                        <span className="flex-1 text-sm dark:text-white truncate">{item.name}</span>
                        <select
                          onChange={(e) => e.target.value && assign(item.kind, item.name, e.target.value)}
                          disabled={busy}
                          defaultValue=""
                          className="text-xs rounded border dark:bg-[#1a1a1a] dark:border-gray-600 dark:text-white px-1 py-1"
                          data-testid={`prov-assign-${item.kind}-${item.name}`}
                        >
                          <option value="">Assign to…</option>
                          {data.provinces.map((p) => (<option key={p} value={p}>{p}</option>))}
                        </select>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                /* From a Province view: show ALL items (with current province), let admin reassign */
                <ProvincePicker data={data} activeProvince={activeProvince} search={search} assign={assign} busy={busy} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ProvincePicker = ({ data, activeProvince, search, assign, busy }) => {
  // Build a flat list of EVERY market/expo/festival with its current province
  const everything = useMemo(() => {
    const list = [];
    Object.entries(data.items || {}).forEach(([prov, buckets]) => {
      Object.entries(buckets).forEach(([kind, names]) => {
        names.forEach((name) => {
          list.push({ name, kind, currentProvince: prov === 'Unassigned' ? null : prov });
        });
      });
    });
    return list;
  }, [data]);

  const q = search.trim().toLowerCase();
  const filtered = q ? everything.filter((i) => i.name.toLowerCase().includes(q)) : everything;

  // Sort: already-in-province first (so admin can also remove), then unassigned, then others
  const sorted = [...filtered].sort((a, b) => {
    const aHere = a.currentProvince === activeProvince ? 0 : (a.currentProvince ? 2 : 1);
    const bHere = b.currentProvince === activeProvince ? 0 : (b.currentProvince ? 2 : 1);
    if (aHere !== bHere) return aHere - bHere;
    return a.name.localeCompare(b.name);
  });

  if (sorted.length === 0) {
    return <p className="text-sm text-gray-500 py-8 text-center">No items match.</p>;
  }

  return (
    <div className="space-y-1">
      {sorted.map((item) => {
        const here = item.currentProvince === activeProvince;
        const elsewhere = item.currentProvince && !here;
        const checkboxState = here ? 'checked' : 'unchecked';
        return (
          <label
            key={`p-${item.kind}-${item.name}`}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${
              here ? 'bg-[#FFD12D]/15 border-[#FFD12D]/40' : 'bg-white dark:bg-[#1a1a1a] dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#252525]'
            }`}
          >
            <input
              type="checkbox"
              checked={checkboxState === 'checked'}
              onChange={() => assign(item.kind, item.name, here ? null : activeProvince)}
              disabled={busy}
              className="w-4 h-4 accent-[#FFD12D]"
            />
            <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${KIND_LABEL[item.kind].color}`}>
              {KIND_LABEL[item.kind].singular}
            </span>
            <span className="flex-1 text-sm dark:text-white truncate">{item.name}</span>
            {elsewhere && (
              <span className="text-[11px] text-gray-500 dark:text-gray-400 italic shrink-0">
                {item.currentProvince}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
};

export default ProvincesModal;
